import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { filterRsvpPage, shouldHideRsvpPage } from '@apps/guest/packages/layout-engine/common/utils/rsvpPageVisibility';
import { useRsvpDeadlineGuard } from '@apps/guest/packages/layout-engine/common/utils/useRsvpDeadlineGuard';
import { filterMigratedLegacyPages } from '@shared/utils/migratedCustomPages';
import { EventPageType } from '@graphql/generated-base';
import { useIsMapPageVisible, withoutHiddenMapPage } from '@shared/hooks/weddingMapPageVisibility';

type UseGuestSitePagesReturn = Readonly<{
  /** The pages every guest-facing nav surface — and Brannan's page resolution — should see. */
  pages: ReadonlyArray<EventPageFragment>;
  /** True while the migrated-custom-page feature flags are still resolving. */
}>;

/**
 * The one page list a layout should render from. Composes the two filters every layout needs:
 * legacy pages migrated to custom pages are dropped, and the RSVP page is dropped when it is
 * effectively disabled or past its deadline.
 *
 * Layouts previously hand-assembled this three-stage pipeline, which is how two of the three ended
 * up with the same intermediate spelled differently (LEGRSVP-96). Both filters preserve the input
 * array by reference on a no-op, so the result stays referentially stable for `useAlohaScroll`.
 */
export const useGuestSitePages = (pages: ReadonlyArray<EventPageFragment>, hasEnabledLegacyRsvpQuestions: Maybe<boolean> | undefined): UseGuestSitePagesReturn => {
  const pagesWithoutMigratedLegacy = useMemo(() => filterMigratedLegacyPages(pages), [pages]);
  // event_service still serves the retired Tidbits row to events that keep tidbit Q&As without the migrated story page.
  // Returns the input by reference when there is nothing to drop, so downstream identity checks stay stable.
  const pagesWithoutTidbits = useMemo(() => {
    const next = pagesWithoutMigratedLegacy.filter(page => page.type !== EventPageType.tidbits);
    return next.length === pagesWithoutMigratedLegacy.length ? pagesWithoutMigratedLegacy : next;
  }, [pagesWithoutMigratedLegacy]);
  /*
    The Wedding Map page is dropped for anyone outside the rollout.

    Here rather than in the nav components because this is the list every
    guest-site surface renders from — the two navs, the hamburger drawer and
    the mobile top bar. Gating the components instead covered two of the four,
    and the two it missed filter by slug, so a map page would still have been
    listed for a guest outside the rollout — a nav entry to a page that this
    same filter keeps out of the page list, so following it lands nowhere.

    This hook is not the only page list a nav is built from: the Registry
    guest page renders the same Brannan nav from `useGetGuestSitePages`, which
    applies the same filter for the same reason.

    `withoutHiddenMapPage` preserves the array reference when nothing drops,
    which is every event today, so `useAlohaScroll` sees no change. It is
    shared with the Registry nav for exactly that reason — this call site had
    reimplemented it inline, which is the drift the helper exists to prevent.
  */
  const isMapPageVisible = useIsMapPageVisible();
  const pagesWithoutHiddenMap = useMemo(() => withoutHiddenMapPage(pagesWithoutTidbits, isMapPageVisible), [pagesWithoutTidbits, isMapPageVisible]);

  const { isRsvpClosedByDeadline } = useRsvpDeadlineGuard();

  // Deliberately memoized on the collapsed decision, not on the two reasons behind it: the reasons
  // resolve at different times, and once one of them hides the RSVP page the other flipping true
  // must not re-filter into a fresh, identical array. See `shouldHideRsvpPage`.
  const isRsvpPageHidden = shouldHideRsvpPage({ hasEnabledLegacyRsvpQuestions, isRsvpClosedByDeadline });
  const guestSitePages = useMemo(() => filterRsvpPage(pagesWithoutHiddenMap, isRsvpPageHidden), [pagesWithoutHiddenMap, isRsvpPageHidden]);

  return { pages: guestSitePages };
};
